import React, { useState, FormEvent, useEffect } from 'react';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pen, Save, Info } from 'lucide-react';
import { toast as sonnerToast } from "sonner";

interface WorkspaceNameCardProps {
    workspaceId: string;
    currentName: string;
    onUpdate: () => Promise<void>;
}

export const WorkspaceNameCard: React.FC<WorkspaceNameCardProps> = ({ workspaceId, currentName, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState(currentName);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setNewName(currentName);
    }, [currentName]);

    const handleRename = async (e: FormEvent) => {
        e.preventDefault();
        if (newName.trim() === currentName || newName.trim().length < 3) {
            setIsEditing(false);
            return;
        }
        setIsSaving(true);
        try {
            const renameFn = httpsCallable(firebaseFunctions, 'renameWorkspace');
            await renameFn({ workspaceId, newName: newName.trim() });
            sonnerToast.success("Workspace renamed!");
            await onUpdate();
            setIsEditing(false);
        } catch (error: any) {
            sonnerToast.error(`Failed to rename: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card>
            <CardHeader>
              <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Workspace Name</CardTitle>
              <CardDescription className="text-slate-600">Manage the name for the "{currentName}" workspace.</CardDescription>
            </CardHeader>
            <CardContent>
                {!isEditing ? (
                    <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-900">{currentName}</p>
                        <Button variant="outline" size="sm" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50" onClick={() => setIsEditing(true)}><Pen className="mr-2 h-4 w-4" />Rename</Button>
                    </div>
                ) : (
                    <form onSubmit={handleRename} className="space-y-4">
                        <div className="space-y-2">
                            <Input className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300" value={newName} onChange={(e) => setNewName(e.target.value)} disabled={isSaving} autoFocus />
                            <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                               <Info size={12} className="mt-0.5 shrink-0" />
                               <p>This name may be visible to clients if you send them a connection request link.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="ghost" className="rounded-xl hover:bg-slate-50" onClick={() => setIsEditing(false)} disabled={isSaving}>Cancel</Button>
                            <Button type="submit" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800" disabled={isSaving}><Save className="mr-2 h-4 w-4" />Save</Button>
                        </div>
                    </form>
                )}
            </CardContent>
        </Card>
    );
};
